import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { ThemeStateService } from '@sites/site-theme/src/state';
import {
    type FormButtonSettingsFormGroupType,
    type FormButtonSettingsFormType,
} from '@components/sp-form-button-settings/src/sp-form-button-settings.type';
import { ERadioGroupType } from '@ui/sp-radio-group';
import { EButtonStyleType, type ESize } from '@common/enums';
import { isEmptyButtonLabel } from '@common/helpers/forms';
import { ButtonMode, type CustomButtonEventPayload, SpCustomButtonModule } from '@components/sp-custom-button';
import type { ButtonIcon } from '@components/sp-button-icon/type/button-icon.type';
import type { ButtonAdditionalEffectsSettings } from '@components/sp-button-additional-effects-settings/type/button-additional-effects-settings.type';
import { CommonModule } from '@angular/common';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { SpButtonIconComponent } from '@components/sp-button-icon';

@Component({
    selector: 'web-next-page-settings',
    templateUrl: './next-page-settings.component.html',
    styleUrls: ['./next-page-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, CommonElementsModule, SpCustomButtonModule, SpButtonIconComponent],
})
export class NextPageSettingsComponent {
    @Input() nextPageFormGroup: FormButtonSettingsFormGroupType;

    private readonly themeState = inject(ThemeStateService);

    public readonly selectedThemeColors = this.themeState.selectedColors;

    public get isShowButton(): FormButtonSettingsFormType['isShowButton'] {
        return this.nextPageFormGroup.get('isShowButton') as FormButtonSettingsFormType['isShowButton'];
    }

    public get label(): FormButtonSettingsFormType['label'] {
        return this.nextPageFormGroup.get('label') as FormButtonSettingsFormType['label'];
    }

    /** Only meaningful while the button is shown — the parent gates Save on the same rule. */
    public get isEmptyLabel(): boolean {
        return this.isShowButton.value && isEmptyButtonLabel(this.label?.value);
    }

    public get styleType(): FormButtonSettingsFormType['styleType'] {
        return this.nextPageFormGroup.get('styleType') as FormButtonSettingsFormType['styleType'];
    }

    public get size(): FormButtonSettingsFormType['size'] {
        return this.nextPageFormGroup.get('size') as FormButtonSettingsFormType['size'];
    }

    public get buttonBase(): FormButtonSettingsFormType['buttonBase'] {
        return this.nextPageFormGroup.get('buttonBase') as FormButtonSettingsFormType['buttonBase'];
    }

    public get buttonHover(): FormButtonSettingsFormType['buttonHover'] {
        return this.nextPageFormGroup.get('buttonHover') as FormButtonSettingsFormType['buttonHover'];
    }

    public get buttonIcon(): FormButtonSettingsFormType['buttonIcon'] {
        return this.nextPageFormGroup.get('buttonIcon') as FormButtonSettingsFormType['buttonIcon'];
    }

    public get additionalEffects(): FormButtonSettingsFormType['additionalEffects'] {
        return this.nextPageFormGroup.get('additionalEffects') as FormButtonSettingsFormType['additionalEffects'];
    }

    public setSize(value: string) {
        return this.size.setValue(value as ESize);
    }

    public customButtonChangeHandler(changes: CustomButtonEventPayload): void {
        this.buttonBase.setValue(changes.base);
        this.buttonHover.setValue(changes.hover);
    }

    public setIsShowButton(value: boolean): void {
        this.isShowButton.patchValue(value);
    }

    public buttonIconChangeHandler(changes: ButtonIcon): void {
        this.buttonIcon.setValue(changes);
    }

    public additionalEffectsChangeHandler(changes: ButtonAdditionalEffectsSettings): void {
        this.additionalEffects.setValue(changes);
    }

    protected readonly ERadioGroupType = ERadioGroupType;
    protected readonly EButtonStyleType = EButtonStyleType;
    protected readonly ButtonMode = ButtonMode;
}
